import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, CircularProgress, Chip, Divider, Typography, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import TuneIcon from '@material-ui/icons/Tune';
import { updateNodeProps, applyColumnLayout } from '../../api/searchExperienceSlice';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import { useComponentRegistry } from '../../core/registry';
import DynamicPropertiesForm, { COMMON_PROPERTY_NAMES } from '../properties/DynamicPropertiesForm';
import styles from './PropertiesEditor.module.scss';
import ColumnsEditor from './ColumnsEditor';
import { componentLabel } from '../../core/registry/componentLabels';

const LAYOUT_FIELDS = new Set(['columns', 'gap', 'padding', 'orientation', 'align', 'position']);
const COMMON_FIELDS = new Set(COMMON_PROPERTY_NAMES);
const CONTENT_ORDER = ['hidden', 'title', 'text', 'label', 'maxItems', 'productsInRow', 'cardType', 'showPrice', 'showImage'];

/** Contextual inspector shared by the canvas editors. */
const PropertiesEditor = ({ selectedNode, view, screen, device, onClose, onBack, onAdd, disabled = false, presentation = 'contextual' }) => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const currentDevice = useSelector(state => state.searchExperience.currentDevice);
  const { registry, loading } = useComponentRegistry();
  const close = onClose || onBack;

  if (!selectedNode) {
    return (
      <div className={styles.empty}>
        <TuneIcon />
        <h3>{t('searchInterface.properties.title')}</h3>
        <p>{t('searchInterface.inspector.selectHint')}</p>
      </div>
    );
  }

  const descriptor = registry?.[selectedNode.type] || {};
  const values = selectedNode.props || {};
  // Preserve properties absent from older registries or owned by custom blocks.
  const properties = { ...Object.fromEntries(Object.keys(values).map(key => [key, {}])), ...(descriptor.properties || {}) };
  const keys = Object.keys(properties).filter(key => selectedNode.type !== 'columns' || !['columns', 'ratio'].includes(key));
  const contentKeys = keys.filter(key => COMMON_FIELDS.has(key) && !LAYOUT_FIELDS.has(key))
    .sort((left, right) => {
      const leftIndex = CONTENT_ORDER.indexOf(left);
      const rightIndex = CONTENT_ORDER.indexOf(right);
      return (leftIndex === -1 ? CONTENT_ORDER.length : leftIndex) - (rightIndex === -1 ? CONTENT_ORDER.length : rightIndex);
    });
  const layoutKeys = keys.filter(key => LAYOUT_FIELDS.has(key));
  const advancedKeys = keys.filter(key => !COMMON_FIELDS.has(key));
  const name = componentLabel(selectedNode.type, t);

  const handlePropChange = (key, value) => {
    if (disabled) return;
    dispatch(updateNodeProps({
      view,
      screen,
      device: device || currentDevice,
      nodeId: selectedNode.id,
      props: {
        ...values,
        [key]: value,
      },
    }));
  };
  const columnEditor = selectedNode.type === 'columns' && <ColumnsEditor node={selectedNode} disabled={disabled} onLayoutChange={weights => {
    if (!disabled) dispatch(applyColumnLayout({ view, screen, device: device || currentDevice, nodeId: selectedNode.id, weights }));
  }} onChange={props => {
    if (!disabled) dispatch(updateNodeProps({ view, screen, device: device || currentDevice, nodeId: selectedNode.id, props }));
  }} />;
  const renderFields = fieldKeys => (
    <DynamicPropertiesForm
      key={`${view}:${screen}:${device || currentDevice}:${selectedNode.id}`}
      componentType={selectedNode.type}
      properties={properties}
      fieldKeys={fieldKeys}
      values={values}
      onChange={handlePropChange}
      disabled={disabled}
    />
  );

  if (presentation === 'original') return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography variant="h6">{t('searchInterface.properties.title')}</Typography>
        {close && <IconButton size="small" onClick={close} aria-label={t('experience.design.closeSettings')}><CloseIcon /></IconButton>}
      </div>
      <div className={styles.content}>
        {loading ? <CircularProgress size={24} /> : <>
          <Chip label={selectedNode.type} size="small" color="primary" />
          <Typography paragraph color="textSecondary">{descriptor.description}</Typography>
          <Divider />
          <section className={styles.originalFields}>
            <h3>{t('searchInterface.properties.componentSettings')}</h3>
            {columnEditor}
            {renderFields(keys)}
          </section>
          <Divider />
          <Typography variant="caption" color="textSecondary">{t('searchInterface.properties.nodeId')} {selectedNode.id}</Typography>
        </>}
      </div>
    </div>
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <span className={styles.eyebrow}>{t('searchInterface.properties.title')}</span>
          <h2>{name}</h2>
        </div>
        <div className={styles.headerActions}>
          {onAdd && <Tooltip title={t('experience.design.addBlockHere')}><span><IconButton size="small" onClick={onAdd} disabled={disabled} aria-label={t('experience.design.addBlockHere')}><AddIcon /></IconButton></span></Tooltip>}
          {close && <IconButton size="small" onClick={close} className={styles.close} aria-label={t('experience.design.closeSettings')}><CloseIcon /></IconButton>}
        </div>
      </div>
      <div className={styles.content}>
        {loading ? <div className={styles.loading}><CircularProgress size={24} /></div> : (
          <>
            {contentKeys.length > 0 && <section className={styles.section} aria-label={t('searchInterface.inspector.content')}>{renderFields(contentKeys)}</section>}
            {(layoutKeys.length > 0 || columnEditor) && (
              <section className={styles.section} aria-label={t('searchInterface.inspector.layout')}>
                <h3>{t('searchInterface.inspector.layout')}</h3>
                {columnEditor}
                {renderFields(layoutKeys)}
              </section>
            )}
            {!contentKeys.length && !layoutKeys.length && !columnEditor && <p className={styles.hint}>{t('searchInterface.inspector.noCommonSettings')}</p>}
            {advancedKeys.length > 0 && <details className={styles.advanced}>
              <summary>{t('searchInterface.inspector.advanced')}</summary>
              <div className={styles.advancedContent}>
                <p className={styles.hint}>{t('searchInterface.inspector.advancedHint')}</p>
                {advancedKeys.length > 0 && renderFields(advancedKeys)}
              </div>
            </details>}
          </>
        )}
      </div>
    </div>
  );
};

export default PropertiesEditor;
